import { Component, inject, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { AuthApiService } from '../../../../core/services/api/auth-api.service';
import { Modal, passwordStrengthValidator } from '../../../../shared';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

function passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
  const newPassword = group.get('newPassword')?.value;
  const confirmPassword = group.get('confirmPassword')?.value;

  return newPassword && confirmPassword && newPassword !== confirmPassword ? { passwordMismatch: true } : null;
}

@Component({
  selector: 'app-change-password-form',
  imports: [CommonModule, ReactiveFormsModule, Modal, TranslocoPipe],
  templateUrl: './change-password-form.html',
})
export class ChangePasswordForm {
  readonly MAX_PASSWORD_LENGTH = 64;

  userId = signal<string | null>(null);
  back = output();
  modal = inject(NgbActiveModal);

  readonly authApiService = inject(AuthApiService);
  readonly fb = inject(FormBuilder);
  readonly transloco = inject(TranslocoService);

  isLoading = signal(false);
  errorMessage = signal('');
  successMessage = signal('');

  showoldPassword = signal(false);
  showNewPassword = signal(false);
  showConfirmPassword = signal(false);

  form: FormGroup = this.fb.group(
    {
      oldPassword: ['', [Validators.required, Validators.maxLength(this.MAX_PASSWORD_LENGTH)]],
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(this.MAX_PASSWORD_LENGTH),
          passwordStrengthValidator(),
        ],
      ],
      confirmPassword: ['', [Validators.required, Validators.maxLength(this.MAX_PASSWORD_LENGTH)]],
    },
    { validators: passwordMatchValidator },
  );

  isInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!(control?.invalid && control.touched);
  }

  getNewPasswordError(): string {
    const errors = this.form.get('newPassword')?.errors;

    if (!errors) {
      return '';
    }

    if (errors['required']) {
      return this.transloco.translate('validation.fieldRequired');
    }

    if (errors['minlength']) {
      return this.transloco.translate('profile.changePassword.newPasswordMinLength');
    }

    if (errors['maxlength']) {
      return this.transloco.translate('validation.maxLength', { max: errors['maxlength'].requiredLength });
    }

    if (errors['minUppercase']) {
      return this.transloco.translate('validation.minUppercase');
    }

    if (errors['minLowercase']) {
      return this.transloco.translate('validation.minLowercase');
    }

    if (errors['minNumber']) {
      return this.transloco.translate('validation.minNumber');
    }

    if (errors['minSpecialChar']) {
      return this.transloco.translate('validation.minSpecialChar');
    }

    if (errors['sequentialChars']) {
      return this.transloco.translate('validation.sequentialChars');
    }

    return this.transloco.translate('profile.changePassword.newPasswordMinLength');
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const userId = this.userId();

    if (!userId) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    const { oldPassword, newPassword } = this.form.value;
    const result = await this.authApiService.changePassword(oldPassword, newPassword);

    this.isLoading.set(false);

    if (result.success) {
      this.successMessage.set(this.transloco.translate('profile.changePassword.successMessage'));
      this.form.reset();
    } else {
      this.errorMessage.set(result.error ?? this.transloco.translate('profile.changePassword.errorDefault'));
    }
  }
}
